import { InternalMod, OligoItem, SkuClientParamDef, SkuTextInputClientParamDef } from '../models/index';

import { API_ENDPOINTS, AppConfigService } from './app-config-service';
import { UserDetails, UserDetailsService } from './user-details-service';

import { countSequenceLength } from '../models/util';

type ProductRequest = {
  catalogNumber: string;
  quantity: number;
};

type PricingRequest = {
  countryCode: string;
  partnerId?: number;
  supplierId?: number;
  customerType?: number;
  accountNumber?: number;
  items: ProductRequest[];
};

export type PriceRuleType = string;
export type PriceAccessStatus = 'Orderable' | 'ContactUs' | '***' | 'Distributor' | 'RFQ' | 'PVNO' | 'LearnWhereToBuy';

type GenericMap<T> = { [key: string]: T };

type ProductMap = GenericMap<ProductRequest>;

type SkuQuantityMap = GenericMap<number>;

type PricingResultMap = GenericMap<PricingResult>;

type PriceDetails<T extends any> = {
  listPrice: T;
  finalPrice: T;
  onlinePrice?: T;
  yourPrice?: T;
};

type ProductResponse = {
  catalogNumber: string;
  formattedPrice?: PriceDetails<string>;
  unFormattedPrice?: PriceDetails<number>;
  quantity?: number;
  priceRuleType?: PriceRuleType;
  priceAccess: {
    status: PriceAccessStatus;
  };
  currency?: string;
  supplier?: {
    supplierId: number;
    supplierName: string;
    fulfillmentSupplierId: number;
    currency: string;
  };
};

type PricingMessage = {
  code: number;
  defaultMessage: string;
};

type PricingResponse = {
  pricing: {
    products: ProductResponse[];
  }
  status: string;
  message: PricingMessage[];
  responseMetaData: {
    processingTime: number;
  };
};

export type PricingResult = {
  currency: string;
  amount: number;
  available: boolean;
  products?: ProductResponse[];
};

export interface PricingService {
  priceForOligo(oligo: OligoItem): ng.IPromise<PricingResult>;
  pricesForOligos(oligos: OligoItem[]): ng.IPromise<PricingResultMap>;
  pricesForSkus(skuQuantityMap: SkuQuantityMap): ng.IPromise<ProductResponse[]>;
}

export const PRICE_RULE_TYPES: {
  ACCOUNT: PriceRuleType;
  LIST: PriceRuleType;
} = {
  ACCOUNT: 'ACCOUNT',
  LIST: 'LIST',
};

export const PRICE_ACCESS_STATUSES: {
  ORDERABLE: PriceAccessStatus;
  CONTACT_US: PriceAccessStatus;
  NO_PRICE: PriceAccessStatus;
  DISTRIBUTOR: PriceAccessStatus;
  RFQ: PriceAccessStatus;
  PVNO: PriceAccessStatus;
  LWTB: PriceAccessStatus;
} = {
  ORDERABLE: 'Orderable',
  CONTACT_US: 'ContactUs',
  NO_PRICE: '***',
  DISTRIBUTOR: 'Distributor',
  RFQ: 'RFQ',
  PVNO: 'PVNO',
  LWTB: 'LearnWhereToBuy',
};

function hasSkuDefined(selected: SkuClientParamDef<string>): boolean {
  return typeof selected.sku === 'string' && selected.sku.length > 0 && selected.sku !== '-';
}

function scaleToProductBasedOnOligos(oligoValues: SkuTextInputClientParamDef[]): (scaleDef: SkuClientParamDef<string>) => ProductRequest {
  return (scaleDef: SkuClientParamDef<string>): ProductRequest => {
    return {
      catalogNumber: scaleDef.sku,
      quantity: oligoValues.reduce((total: number, paramDef: SkuTextInputClientParamDef) => {
        total = total + countSequenceLength(paramDef.value);
        return total;
      }, 0),
    };
  };
}

function internalModProductsFromOligo(paramDef: SkuTextInputClientParamDef): ProductRequest[] {
  const { internalMod: internalModSkuMap } = paramDef;
  const internalModProducts: ProductRequest[] = internalModSkuMap
    .filter((im: InternalMod) => typeof im.sku === 'string' && im.sku.length > 0 && im.quantity > 0)
    .map((im: InternalMod) => {
      return { catalogNumber: im.sku, quantity: im.quantity };
    });
  const internalModsMap: ProductMap = mergeSameProducts({}, internalModProducts);
  return Object.keys(internalModsMap).map((sku: string) => internalModsMap[sku]);
}

function mergeSameProducts(acc: ProductMap, nextProducts: ProductRequest[]): ProductMap {
  nextProducts.forEach((product: ProductRequest) => {
    const { catalogNumber } = product;
    const existProduct: ProductRequest = acc[catalogNumber];
    if (existProduct) {
      existProduct.quantity = existProduct.quantity + product.quantity;
    } else {
      acc[catalogNumber] = { ...product };
    }
  });
  return acc;
}

function productsForOligo(oligo: OligoItem): ProductRequest[] {
  const { definition } = oligo;
  const { basic, advanced } = definition;
  const { scale, purification, format, delivery, prime3Mod, prime5Mod } = basic;
  const { alternate, handling } = advanced;
  const { oligoValue } = basic;
  // we have to compute for pricing including the price
  // if the user selected alternate oligos
  const oligoValues: SkuTextInputClientParamDef[] = [oligoValue, ...alternate.selected]
    .filter((paramDef: SkuTextInputClientParamDef) => typeof paramDef.value === 'string');
  // the cost of an oligo is based on the number
  // of bases with the cost of the selected scale sku
  const scaleProducts: ProductRequest[] = [scale.selected]
    .filter(hasSkuDefined)
    .map(scaleToProductBasedOnOligos(oligoValues));
  const internalModProductMap: ProductMap = oligoValues
    .map(internalModProductsFromOligo)
    .reduce(mergeSameProducts, {});
  const internalModProducts: ProductRequest[] = Object.keys(internalModProductMap)
    .map((key: string) => internalModProductMap[key]);

  const selectionProducts: ProductRequest[] = [
    purification.selected,
    format.selected,
    delivery.selected,
    prime3Mod.selected,
    prime5Mod.selected,
    ...handling.selected,
  ].filter(hasSkuDefined).map((selected: SkuClientParamDef<string>): ProductRequest => {
    return {
      catalogNumber: selected.sku,
      quantity: oligoValues.length,
    };
  });
  return [ ...scaleProducts, ...selectionProducts, ...internalModProducts];
}

function hasPriceInfo(product: ProductResponse): boolean {
  return product.unFormattedPrice ? typeof product.unFormattedPrice === 'object' : false;
}

function productPrice(priceDetails: PriceDetails<number>): number {
  const { listPrice, finalPrice } = priceDetails;
  const targetPrice: number = typeof finalPrice === 'number' ? finalPrice : listPrice;
  return targetPrice;
}

function isPriceViewable(product: ProductResponse): boolean {
  const { priceAccess } = product;
  const { status } = priceAccess;
  const hasPriceViewStatus: boolean = status === PRICE_ACCESS_STATUSES.ORDERABLE ||
                                      status === PRICE_ACCESS_STATUSES.PVNO;
  // in exceptional cases where status is not orderable
  // but the product otherwise shows a price of 0, we
  // treat it as an orderable product
  if (hasPriceInfo(product)) {
    const { unFormattedPrice } = product;
    const targetPrice: number = productPrice(unFormattedPrice as PriceDetails<number>);
    const hasZeroPrice: boolean = targetPrice === 0;
    return hasPriceViewStatus || hasZeroPrice;
  }
  return hasPriceViewStatus;
}

function pricingResultFromProducts(products: ProductResponse[], skuQuantityMap: SkuQuantityMap): PricingResult {
  let pricingResult: PricingResult = {
    available: true,
    amount: 0,
    currency: 'USD',
  };
  products = products || [];
  if (products.length > 0) {
    pricingResult = products.reduce((result: PricingResult, product: ProductResponse) => {
      const showPrice: boolean = isPriceViewable(product);
      if (showPrice) {
        const { unFormattedPrice, catalogNumber, supplier } = product;
        const currency: string = supplier ? supplier.currency : product.currency as string;
        const quantity: number = skuQuantityMap[catalogNumber];
        const targetPrice: number = productPrice(unFormattedPrice as PriceDetails<number>);
        result.amount = result.amount + (targetPrice * quantity);
        result.currency = currency;
      }
      result.available = result.available && showPrice;
      return result;
    }, pricingResult);
  } else {
    pricingResult.available = false;
  }
  return pricingResult;
}

type PricingDataMap = {
  allSkus: SkuQuantityMap;
  skusPerOligo: GenericMap<SkuQuantityMap>;
};

class PricingServiceImpl implements PricingService {

  private userDetails?: UserDetails;

  constructor(private $q: ng.IQService,
              private $http: ng.IHttpService,
              private configService: AppConfigService,
              private userDetailsService: UserDetailsService) {
  }

  public pricesForOligos(oligos: OligoItem[]): ng.IPromise<PricingResultMap> {
    const rootDeferred: ng.IDeferred<PricingResultMap> = this.$q.defer();
    const pricingDataMap: PricingDataMap = oligos.reduce((acc: PricingDataMap, oligo: OligoItem) => {
      const { id } = oligo;
      const { allSkus, skusPerOligo } = acc;
      const products: ProductRequest[] = productsForOligo(oligo);
      const skuQuantityMap: SkuQuantityMap = products.reduce((skuQuantityMapForOligo: SkuQuantityMap, product: ProductRequest) => {
        const { catalogNumber, quantity } = product;
        skuQuantityMapForOligo[catalogNumber] = quantity;
        return skuQuantityMapForOligo;
      }, {} as SkuQuantityMap);
      skusPerOligo[id] = skuQuantityMap;
      Object.keys(skuQuantityMap).forEach((sku: string) => {
        // we don't care about the quantity we send to the API,
        // we just want the price per sku
        allSkus[sku] = 1;
      });
      return acc;
    }, { allSkus: {}, skusPerOligo: {} } as PricingDataMap);
    const { allSkus: allSkusMap, skusPerOligo: skusPerOligoMap } = pricingDataMap;
    let pricingResultMap: PricingResultMap = {};
    this.pricesForSkus(allSkusMap).then((products: ProductResponse[]) => {
      pricingResultMap = Object.keys(skusPerOligoMap).reduce((acc: PricingResultMap, oligoId: string) => {
        const skuQuantityMapForThisOligo: SkuQuantityMap = skusPerOligoMap[oligoId];
        const productsForThisOligo: ProductResponse[] = products.filter((product) => {
          const { catalogNumber } = product;
          const skuPresentInThisOligo: boolean = typeof skuQuantityMapForThisOligo[catalogNumber] === 'number';
          return skuPresentInThisOligo;
        });
        const pricingResult: PricingResult = pricingResultFromProducts(productsForThisOligo, skuQuantityMapForThisOligo);
        acc[oligoId] = pricingResult;
        return acc;
      }, {} as PricingResultMap);
      rootDeferred.resolve(pricingResultMap);
    }, (e) => {
      console.error(e);
      const pricingResult: PricingResult = {
        amount: 0,
        available: false,
        currency: '',
      };
      pricingResultMap = oligos.reduce((acc: PricingResultMap, oligo: OligoItem) => {
        const { id } = oligo;
        acc[id] = { ...pricingResult };
        return acc;
      }, {} as PricingResultMap);
      rootDeferred.resolve(pricingResultMap);
    });
    return rootDeferred.promise;
  }

  public priceForOligo(oligo: OligoItem): ng.IPromise<PricingResult> {
    const rootDeferred: ng.IDeferred<PricingResult> = this.$q.defer();
    this.pricesForOligos([oligo]).then((pricingMap: PricingResultMap) => {
      rootDeferred.resolve(pricingMap[oligo.id]);
    }, rootDeferred.reject);
    return rootDeferred.promise;
  }

  public pricesForSkus(skuQuantityMap: SkuQuantityMap): ng.IPromise<ProductResponse[]> {
    const rootDeferred: ng.IDeferred<ProductResponse[]> = this.$q.defer();
    const getUserDetails: () => ng.IPromise<UserDetails> = () => {
      const deferred: ng.IDeferred<UserDetails> = this.$q.defer();
      if (!this.userDetails) {
        this.userDetailsService.getUserDetails().then((userDetails: UserDetails) => {
          this.userDetails = userDetails;
          deferred.resolve(userDetails);
        }, deferred.reject);
      } else {
        deferred.resolve(this.userDetails);
      }
      return deferred.promise;
    };
    getUserDetails().then((userDetails: UserDetails) => {
      const path: string = this.configService.pathFor(API_ENDPOINTS.GET_PRICE);
      const pricingRequest: PricingRequest = Object.keys(skuQuantityMap).reduce((acc: PricingRequest, catalogNumber: string) => {
        const { items } = acc;
        const quantity: number = skuQuantityMap[catalogNumber];
        items.push({ catalogNumber, quantity });
        return acc;
      }, { countryCode: userDetails.countryCode, items: []} as PricingRequest);
      this.$http.post<PricingResponse>(path, pricingRequest).then((response: ng.IHttpResponse<PricingResponse>) => {
        const { status, data } = response;
        if (status === 200) {
          const { pricing } = data;
          const { products } = pricing;
          rootDeferred.resolve(products);
        } else {
          throw new Error(`Unable to retrieve prices with status code: ${status}`); // let catch handle it
        }
      }, rootDeferred.reject);
    }, rootDeferred.reject);
    return rootDeferred.promise;
  }

}

type SkuPriceMap = GenericMap<ProductResponse>;

function skusNotInCache(target: SkuQuantityMap, cache: SkuPriceMap): SkuQuantityMap {
  return Object.keys(target).reduce((acc: SkuQuantityMap, sku: string) => {
    const priceInfo: ProductResponse = cache[sku];
    if (!priceInfo) {
      acc[sku] = target[sku];
    }
    return acc;
  }, {} as SkuQuantityMap);
}

class InMemoryCachePricingServiceDecorator implements PricingService {

  private priceMap: SkuPriceMap = {};
  private remotePriceForSkus: (skuQuantityMap: SkuQuantityMap) => ng.IPromise<ProductResponse[]>;

  constructor(private $q: ng.IQService, private delegate: PricingService) {
    this.remotePriceForSkus = delegate.pricesForSkus.bind(delegate);
    delegate.pricesForSkus = this.pricesForSkus.bind(this);
  }

  public pricesForOligos(oligos: OligoItem[]): ng.IPromise<PricingResultMap> {
    return this.delegate.pricesForOligos(oligos);
  }

  public priceForOligo(oligo: OligoItem): ng.IPromise<PricingResult> {
    return this.delegate.priceForOligo(oligo);
  }

  public pricesForSkus(skuQuantityMap: SkuQuantityMap): ng.IPromise<ProductResponse[]> {
    const { priceMap } = this;
    const rootDeferred: ng.IDeferred<ProductResponse[]> = this.$q.defer();
    const oligoSkusNotInCache: SkuQuantityMap = skusNotInCache(skuQuantityMap, priceMap);
    const allSkuPricesCached: boolean = Object.keys(oligoSkusNotInCache).length < 1;
    const productPricesFromCache = (): ProductResponse[] => {
      return Object.keys(skuQuantityMap).map((sku: string) => {
        const quantity: number = skuQuantityMap[sku];
        const product: ProductResponse = priceMap[sku];
        return {
          ...product,
          quantity,
        };
      });
    };
    if (allSkuPricesCached) {
      rootDeferred.resolve(productPricesFromCache());
    } else {
      this.remotePriceForSkus(oligoSkusNotInCache).then((products: ProductResponse[]) => {
        if (products) {
          products.forEach((product: ProductResponse) => {
            priceMap[product.catalogNumber] = product;
          });
        }
        rootDeferred.resolve(productPricesFromCache());
      }, rootDeferred.reject);
    }
    return rootDeferred.promise;
  }
}

export const PRICING_SERVICE_NAME = 'pricingService';
export const PricingServiceFactory = ($q: ng.IQService,
                                      $http: ng.IHttpService,
                                      configService: AppConfigService,
                                      userDetailsService: UserDetailsService): PricingService => {
  return new InMemoryCachePricingServiceDecorator($q, new PricingServiceImpl($q, $http, configService, userDetailsService));
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/pricing-service.ts